import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type { AIError } from '@coffeeplexity/shared';
import type { RootState } from '../index';
import { logger } from '../../utils/logger';
import {
  notificationAnalyticsService,
  type NotificationAnalytics,
  type FrequencyOptimization,
  type ContentPersonalization,
  type TimingOptimization,
  type NotificationEffectivenessReport,
} from '../../services/notificationAnalyticsService';

interface NotificationAnalyticsState {
  analytics: NotificationAnalytics | null;
  effectivenessReport: NotificationEffectivenessReport | null;
  performanceDashboard: {
    realTimeMetrics: {
      activeNotifications: number;
      pendingDeliveries: number;
      recentEngagements: number;
      currentEngagementRate: number;
    } | null;
    hourlyBreakdown: Array<{
      hour: number;
      delivered: number;
      opened: number;
      clicked: number;
      engagementRate: number;
    }>;
    channelBreakdown: Array<{
      channel: 'push' | 'email' | 'in_app' | 'sms';
      deliveryRate: number;
      openRate: number;
      clickThroughRate: number;
      engagementScore: number;
      userPreference: number;
      costEffectiveness: number;
      reliability: number;
    }>;
    topPerformingContent: Array<{
      contentType: string;
      engagementRate: number;
      volume: number;
    }>;
    alerts: Array<{
      type: 'performance_drop' | 'frequency_issue' | 'timing_suboptimal';
      message: string;
      severity: 'low' | 'medium' | 'high';
      actionRequired: boolean;
    }>;
  };
  abTests: Array<{
    testId: string;
    status: 'created' | 'running' | 'completed';
    startDate: string;
    estimatedEndDate: string;
    currentResults?: {
      variants: Array<{
        id: string;
        impressions: number;
        conversions: number;
        conversionRate: number;
        confidence: number;
      }>;
      leadingVariant?: string;
      statisticalSignificance: boolean;
    };
  }>;
  optimizations: {
    timing: TimingOptimization | null;
    frequency: FrequencyOptimization | null;
    content: ContentPersonalization | null;
  };
  loading: {
    analytics: boolean;
    dashboard: boolean;
    optimization: boolean;
    report: boolean;
    abTest: boolean;
  };
  error: AIError | null;
  lastUpdated: {
    analytics: string | null;
    dashboard: string | null;
    optimizations: string | null;
    report: string | null;
  };
  cacheExpiry: {
    analytics: string | null;
    dashboard: string | null;
  };
  selectedTimeRange: 'day' | 'week' | 'month' | 'quarter';
}

const initialState: NotificationAnalyticsState = {
  analytics: null,
  effectivenessReport: null,
  performanceDashboard: {
    realTimeMetrics: null,
    hourlyBreakdown: [],
    channelBreakdown: [],
    topPerformingContent: [],
    alerts: [],
  },
  abTests: [],
  optimizations: {
    timing: null,
    frequency: null,
    content: null,
  },
  loading: {
    analytics: false,
    dashboard: false,
    optimization: false,
    report: false,
    abTest: false,
  },
  error: null,
  lastUpdated: {
    analytics: null,
    dashboard: null,
    optimizations: null,
    report: null,
  },
  cacheExpiry: {
    analytics: null,
    dashboard: null,
  },
  selectedTimeRange: 'week',
};

// Async thunks
export const fetchNotificationAnalytics = createAsyncThunk(
  'notificationAnalytics/fetchAnalytics',
  async (
    {
      userId,
      timeRange = 'week',
    }: {
      userId: string;
      timeRange?: 'day' | 'week' | 'month' | 'quarter';
    },
    { rejectWithValue }
  ) => {
    try {
      const analytics =
        await notificationAnalyticsService.getNotificationAnalytics(
          userId,
          timeRange
        );
      return { analytics, timeRange };
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const fetchPerformanceDashboard = createAsyncThunk(
  'notificationAnalytics/fetchDashboard',
  async (
    {
      userId,
      timeRange = 'day',
    }: {
      userId: string;
      timeRange?: 'hour' | 'day' | 'week';
    },
    { rejectWithValue }
  ) => {
    try {
      const dashboard =
        await notificationAnalyticsService.getPerformanceDashboard(
          userId,
          timeRange
        );
      return dashboard;
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const optimizeNotificationTiming = createAsyncThunk(
  'notificationAnalytics/optimizeTiming',
  async (
    {
      userId,
      currentPatterns,
      timingPreferences,
    }: {
      userId: string;
      currentPatterns: any[];
      timingPreferences: any[];
    },
    { rejectWithValue }
  ) => {
    try {
      const optimization =
        await notificationAnalyticsService.optimizeNotificationTiming(
          userId,
          currentPatterns,
          timingPreferences
        );
      return optimization;
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const optimizeNotificationFrequency = createAsyncThunk(
  'notificationAnalytics/optimizeFrequency',
  async (
    {
      userId,
      currentFrequency,
      userFeedback,
      engagementMetrics,
    }: {
      userId: string;
      currentFrequency: number;
      userFeedback: Array<{
        type: 'too_frequent' | 'just_right' | 'too_infrequent';
        timestamp: string;
        context?: any;
      }>;
      engagementMetrics: {
        openRate: number;
        clickThroughRate: number;
        dismissalRate: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const optimization =
        await notificationAnalyticsService.optimizeNotificationFrequency(
          userId,
          currentFrequency,
          userFeedback,
          engagementMetrics
        );
      return optimization;
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const personalizeNotificationContent = createAsyncThunk(
  'notificationAnalytics/personalizeContent',
  async (
    {
      userId,
      engagementHistory,
      userPreferences,
    }: {
      userId: string;
      engagementHistory: any[];
      userPreferences: any;
    },
    { rejectWithValue }
  ) => {
    try {
      const personalization =
        await notificationAnalyticsService.personalizeNotificationContent(
          userId,
          engagementHistory,
          userPreferences
        );
      return personalization;
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const generateEffectivenessReport = createAsyncThunk(
  'notificationAnalytics/generateReport',
  async (
    {
      userId,
      startDate,
      endDate,
    }: {
      userId: string;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const report =
        await notificationAnalyticsService.generateEffectivenessReport(
          userId,
          startDate,
          endDate
        );
      return report;
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const runNotificationABTest = createAsyncThunk(
  'notificationAnalytics/runABTest',
  async (
    {
      userId,
      testConfig,
    }: {
      userId: string;
      testConfig: {
        name: string;
        variants: Array<{
          id: string;
          name: string;
          config: {
            timing?: {
              preferredHours: number[];
              frequency: number;
            };
            content?: {
              tone: 'casual' | 'professional' | 'enthusiastic';
              length: 'short' | 'medium' | 'long';
              includeEmoji: boolean;
            };
            frequency?: {
              multiplier: number;
              maxPerDay: number;
            };
          };
        }>;
        duration: number;
        successMetric:
          | 'engagement_rate'
          | 'click_through_rate'
          | 'satisfaction';
        minimumSampleSize: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const abTest = await notificationAnalyticsService.runNotificationABTest(
        userId,
        testConfig
      );
      return abTest;
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const getABTestResults = createAsyncThunk(
  'notificationAnalytics/getABTestResults',
  async (testId: string, { rejectWithValue }) => {
    try {
      const results =
        await notificationAnalyticsService.getABTestResults(testId);
      return results;
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const trackNotificationPerformance = createAsyncThunk(
  'notificationAnalytics/trackPerformance',
  async (
    {
      notificationId,
      metrics,
    }: {
      notificationId: string;
      metrics: {
        deliveredAt?: string;
        openedAt?: string;
        clickedAt?: string;
        dismissedAt?: string;
        expiredAt?: string;
        context: {
          deviceType: string;
          appState: string;
          batteryLevel?: number;
          networkType?: string;
          location?: {
            latitude: number;
            longitude: number;
          };
        };
      };
    },
    { rejectWithValue }
  ) => {
    try {
      await notificationAnalyticsService.trackNotificationPerformance(
        notificationId,
        metrics
      );
      return { notificationId, metrics };
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

const notificationAnalyticsSlice = createSlice({
  name: 'notificationAnalytics',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearAnalyticsData: state => {
      state.analytics = null;
      state.effectivenessReport = null;
      state.performanceDashboard = {
        realTimeMetrics: null,
        hourlyBreakdown: [],
        channelBreakdown: [],
        topPerformingContent: [],
        alerts: [],
      };
      state.optimizations = {
        timing: null,
        frequency: null,
        content: null,
      };
      state.lastUpdated = {
        analytics: null,
        dashboard: null,
        optimizations: null,
        report: null,
      };
      state.cacheExpiry = {
        analytics: null,
        dashboard: null,
      };
    },
    setSelectedTimeRange: (
      state,
      action: PayloadAction<'day' | 'week' | 'month' | 'quarter'>
    ) => {
      state.selectedTimeRange = action.payload;
    },
    addDashboardAlert: (
      state,
      action: PayloadAction<{
        type: 'performance_drop' | 'frequency_issue' | 'timing_suboptimal';
        message: string;
        severity: 'low' | 'medium' | 'high';
        actionRequired: boolean;
      }>
    ) => {
      state.performanceDashboard.alerts.unshift(action.payload);
      // Keep only the most recent 10 alerts
      if (state.performanceDashboard.alerts.length > 10) {
        state.performanceDashboard.alerts =
          state.performanceDashboard.alerts.slice(0, 10);
      }
    },
    dismissDashboardAlert: (state, action: PayloadAction<number>) => {
      state.performanceDashboard.alerts.splice(action.payload, 1);
    },
    updateABTestStatus: (
      state,
      action: PayloadAction<{
        testId: string;
        status: 'created' | 'running' | 'completed';
        currentResults?: any;
      }>
    ) => {
      const testIndex = state.abTests.findIndex(
        test => test.testId === action.payload.testId
      );
      if (testIndex >= 0) {
        state.abTests[testIndex].status = action.payload.status;
        if (action.payload.currentResults) {
          state.abTests[testIndex].currentResults =
            action.payload.currentResults;
        }
      }
    },
    setCacheExpiry: (
      state,
      action: PayloadAction<{
        type: 'analytics' | 'dashboard';
        expiry: string;
      }>
    ) => {
      state.cacheExpiry[action.payload.type] = action.payload.expiry;
    },
  },
  extraReducers: builder => {
    // Fetch Notification Analytics
    builder
      .addCase(fetchNotificationAnalytics.pending, state => {
        state.loading.analytics = true;
        state.error = null;
      })
      .addCase(fetchNotificationAnalytics.fulfilled, (state, action) => {
        state.loading.analytics = false;
        state.analytics = action.payload.analytics;
        state.selectedTimeRange = action.payload.timeRange;
        state.lastUpdated.analytics = new Date().toISOString();
        state.cacheExpiry.analytics = new Date(
          Date.now() + 30 * 60 * 1000
        ).toISOString(); // 30 minutes
      })
      .addCase(fetchNotificationAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload as AIError;
      });

    // Fetch Performance Dashboard
    builder
      .addCase(fetchPerformanceDashboard.pending, state => {
        state.loading.dashboard = true;
        state.error = null;
      })
      .addCase(fetchPerformanceDashboard.fulfilled, (state, action) => {
        state.loading.dashboard = false;
        state.performanceDashboard = action.payload;
        state.lastUpdated.dashboard = new Date().toISOString();
        state.cacheExpiry.dashboard = new Date(
          Date.now() + 5 * 60 * 1000
        ).toISOString(); // 5 minutes
      })
      .addCase(fetchPerformanceDashboard.rejected, (state, action) => {
        state.loading.dashboard = false;
        state.error = action.payload as AIError;
      });

    // Optimize Notification Timing
    builder
      .addCase(optimizeNotificationTiming.pending, state => {
        state.loading.optimization = true;
        state.error = null;
      })
      .addCase(optimizeNotificationTiming.fulfilled, (state, action) => {
        state.loading.optimization = false;
        state.optimizations.timing = action.payload;
        state.lastUpdated.optimizations = new Date().toISOString();
      })
      .addCase(optimizeNotificationTiming.rejected, (state, action) => {
        state.loading.optimization = false;
        state.error = action.payload as AIError;
      });

    // Optimize Notification Frequency
    builder
      .addCase(optimizeNotificationFrequency.pending, state => {
        state.loading.optimization = true;
        state.error = null;
      })
      .addCase(optimizeNotificationFrequency.fulfilled, (state, action) => {
        state.loading.optimization = false;
        state.optimizations.frequency = action.payload;
        state.lastUpdated.optimizations = new Date().toISOString();
      })
      .addCase(optimizeNotificationFrequency.rejected, (state, action) => {
        state.loading.optimization = false;
        state.error = action.payload as AIError;
      });

    // Personalize Notification Content
    builder
      .addCase(personalizeNotificationContent.pending, state => {
        state.loading.optimization = true;
        state.error = null;
      })
      .addCase(personalizeNotificationContent.fulfilled, (state, action) => {
        state.loading.optimization = false;
        state.optimizations.content = action.payload;
        state.lastUpdated.optimizations = new Date().toISOString();
      })
      .addCase(personalizeNotificationContent.rejected, (state, action) => {
        state.loading.optimization = false;
        state.error = action.payload as AIError;
      });

    // Generate Effectiveness Report
    builder
      .addCase(generateEffectivenessReport.pending, state => {
        state.loading.report = true;
        state.error = null;
      })
      .addCase(generateEffectivenessReport.fulfilled, (state, action) => {
        state.loading.report = false;
        state.effectivenessReport = action.payload;
        state.lastUpdated.report = new Date().toISOString();
      })
      .addCase(generateEffectivenessReport.rejected, (state, action) => {
        state.loading.report = false;
        state.error = action.payload as AIError;
      });

    // Run Notification A/B Test
    builder
      .addCase(runNotificationABTest.pending, state => {
        state.loading.abTest = true;
        state.error = null;
      })
      .addCase(runNotificationABTest.fulfilled, (state, action) => {
        state.loading.abTest = false;
        state.abTests.push(action.payload);
      })
      .addCase(runNotificationABTest.rejected, (state, action) => {
        state.loading.abTest = false;
        state.error = action.payload as AIError;
      });

    // Get A/B Test Results
    builder
      .addCase(getABTestResults.pending, state => {
        state.loading.abTest = true;
        state.error = null;
      })
      .addCase(getABTestResults.fulfilled, (state, action) => {
        state.loading.abTest = false;
        const testIndex = state.abTests.findIndex(
          test => test.testId === action.payload.testId
        );
        if (testIndex >= 0) {
          state.abTests[testIndex].status = action.payload.status;
        }
      })
      .addCase(getABTestResults.rejected, (state, action) => {
        state.loading.abTest = false;
        state.error = action.payload as AIError;
      });

    // Track Notification Performance
    builder
      .addCase(trackNotificationPerformance.pending, state => {
        // Don't set loading state for tracking as it should be silent
      })
      .addCase(trackNotificationPerformance.fulfilled, (state, action) => {
        // Performance tracking completed successfully
        // Could update real-time metrics here if needed
      })
      .addCase(trackNotificationPerformance.rejected, (state, action) => {
        // Log error but don't show to user as tracking is background operation
        logger.warn(
          'Failed to track notification performance:',
          action.payload
        );
      });
  },
});

export const {
  clearError,
  clearAnalyticsData,
  setSelectedTimeRange,
  addDashboardAlert,
  dismissDashboardAlert,
  updateABTestStatus,
  setCacheExpiry,
} = notificationAnalyticsSlice.actions;

// Basic selectors
export const selectNotificationAnalytics = (state: RootState) =>
  state.notificationAnalytics.analytics;
export const selectEffectivenessReport = (state: RootState) =>
  state.notificationAnalytics.effectivenessReport;
export const selectPerformanceDashboard = (state: RootState) =>
  state.notificationAnalytics.performanceDashboard;
export const selectABTests = (state: RootState) =>
  state.notificationAnalytics.abTests;
export const selectOptimizations = (state: RootState) =>
  state.notificationAnalytics.optimizations;
export const selectAnalyticsLoading = (state: RootState) =>
  state.notificationAnalytics.loading;
export const selectAnalyticsError = (state: RootState) =>
  state.notificationAnalytics.error;
export const selectSelectedTimeRange = (state: RootState) =>
  state.notificationAnalytics.selectedTimeRange;

// Memoized selectors
export const selectEngagementMetrics = createSelector(
  [selectNotificationAnalytics],
  analytics => {
    if (!analytics) return null;
    return {
      deliveryRate: analytics.deliveryRate,
      openRate: analytics.openRate,
      clickThroughRate: analytics.clickThroughRate,
      dismissalRate: analytics.dismissalRate,
      engagementScore: analytics.engagementScore,
    };
  }
);

export const selectTimingInsights = createSelector(
  [selectOptimizations],
  optimizations => {
    return optimizations.timing;
  }
);

export const selectFrequencyInsights = createSelector(
  [selectOptimizations],
  optimizations => {
    return optimizations.frequency;
  }
);

export const selectContentInsights = createSelector(
  [selectOptimizations],
  optimizations => {
    return optimizations.content;
  }
);

export const selectActiveABTests = createSelector([selectABTests], abTests => {
  return abTests.filter(test => test.status === 'running');
});

export const selectCompletedABTests = createSelector(
  [selectABTests],
  abTests => {
    return abTests.filter(test => test.status === 'completed');
  }
);

export const selectHighPriorityAlerts = createSelector(
  [selectPerformanceDashboard],
  dashboard => {
    return dashboard.alerts.filter(alert => alert.severity === 'high');
  }
);

export const selectChannelPerformanceRanking = createSelector(
  [selectPerformanceDashboard],
  dashboard => {
    return [...dashboard.channelBreakdown].sort(
      (a, b) => b.engagementScore - a.engagementScore
    );
  }
);

export const selectBestPerformingHours = createSelector(
  [selectPerformanceDashboard],
  dashboard => {
    return [...dashboard.hourlyBreakdown]
      .sort((a, b) => b.engagementRate - a.engagementRate)
      .slice(0, 5);
  }
);

export const selectImprovementOpportunities = createSelector(
  [selectNotificationAnalytics],
  analytics => {
    if (!analytics) return [];
    return analytics.improvementSuggestions.filter(
      suggestion => suggestion.priority === 'high'
    );
  }
);

export const selectAnalyticsTrends = createSelector(
  [selectNotificationAnalytics],
  analytics => {
    if (!analytics) return [];
    return analytics.trends;
  }
);

export const selectOverallEffectivenessScore = createSelector(
  [selectEffectivenessReport],
  report => {
    return report?.overallScore || 0;
  }
);

export const selectKeyInsights = createSelector(
  [selectEffectivenessReport],
  report => {
    return report?.keyInsights || [];
  }
);

export const selectActionableRecommendations = createSelector(
  [selectEffectivenessReport],
  report => {
    return report?.actionableRecommendations || [];
  }
);

export const selectAnalyticsCacheStale = createSelector(
  [
    selectNotificationAnalytics,
    (state: RootState) => state.notificationAnalytics.cacheExpiry.analytics,
  ],
  (analytics, cacheExpiry) => {
    if (!analytics || !cacheExpiry) return true;
    return new Date() > new Date(cacheExpiry);
  }
);

export const selectDashboardCacheStale = createSelector(
  [
    selectPerformanceDashboard,
    (state: RootState) => state.notificationAnalytics.cacheExpiry.dashboard,
  ],
  (dashboard, cacheExpiry) => {
    if (!dashboard.realTimeMetrics || !cacheExpiry) return true;
    return new Date() > new Date(cacheExpiry);
  }
);

export default notificationAnalyticsSlice.reducer;
